import * as AbsintheSocket from '@absinthe/socket';
import { createAbsintheSocketLink } from '@absinthe/socket-apollo-link';
import type { NormalizedCacheObject, QueryResult } from '@apollo/client';
import { ApolloClient, ApolloLink, split } from '@apollo/client';
import { RetryLink } from '@apollo/client/link/retry';
import { getMainDefinition } from '@apollo/client/utilities';
import { createLink } from 'apollo-absinthe-upload-link';
import { RestLink } from 'apollo-link-rest';
import { camelCase, snakeCase } from 'lodash';

import { WebSocketConnectionState } from 'common/constants/webSocketConnectionState';
import { isTokenAvailable, removeTokenFromLocalStorage } from 'common/helpers/jwtHelper';
import { webSocketStateSubject } from 'common/helpers/webSocketState';
import { TrackedError } from 'ErrorBoundary/Error';

import { getApolloCache } from './cache';
import { getGraphQlApiUrl, getPublicApiUrl, getWsUrl } from './config';
import authorizationLink, { getAuthToken } from './links/authorizationLink';
import errorLink from './links/error_link/errorLink';
import retryLinkConfig from './links/retryLink/retryLinkConfig';
import { setupPhoenixSocket } from './PhoenixSocketHelper';

/**
 * Returns an instance of ApolloClient initialized with the given endpoint uri and with middleware for
 * using the token from the local storage for authorization.
 *
 * @returns the initialized ApolloClient.
 */
export async function getApolloClient(): Promise<ApolloClient<NormalizedCacheObject>> {
  const graphQlApiUrl = getGraphQlApiUrl();
  const publicApiURl = getPublicApiUrl();

  /**
   * In order to use fragments on unions and interfaces
   * we have to use IntrospectionFragmentMatcher
   *
   * It requires graphql schema,
   * so we have to fetch it and create new IntrospectionFragmentMatcher
   * before we proceed with creating authorized ApolloClient
   */
  let link;
  const authToken = getAuthToken();

  const restLink = new RestLink({
    uri: publicApiURl,
    headers: {
      'Content-Type': 'application/json',
    },
    fieldNameNormalizer: name => camelCase(name),
    fieldNameDenormalizer: name => snakeCase(name),
  });

  /**
   * Tries to query the GraphQL and create options for authorized ApolloClient
   * if it fails then creates options for unauthorized client
   */
  if (await isAuthTokenValid(graphQlApiUrl, authToken)) {
    const uploadLink = createLink({ uri: graphQlApiUrl });
    const retryLink = new RetryLink(retryLinkConfig);

    const isWsConnAvailable = await checkWsConnAvailability();
    webSocketStateSubject.next(
      isWsConnAvailable ? WebSocketConnectionState.OPEN : WebSocketConnectionState.NOT_AVAILABLE
    );

    const httpLink = ApolloLink.from([retryLink, restLink, authorizationLink, errorLink, uploadLink]);

    if (isWsConnAvailable) {
      const phnxSocket = await setupPhoenixSocket({ fetchWsToken, getWsUrl });

      const wsLink = createAbsintheSocketLink(AbsintheSocket.create(phnxSocket));

      link = split(
        ({ query }) => {
          const definition = getMainDefinition(query);
          return definition.kind === 'OperationDefinition' && definition.operation === 'subscription';
        },
        wsLink,
        httpLink
      );
    } else {
      link = httpLink;
    }
  } else {
    /**
     * If we fail to query GraphQL that means that we have a problem with JWT
     * and user is not authorized, so we remove invalid JWT item from local storage
     */
    removeTokenFromLocalStorage();
    link = ApolloLink.from([restLink, errorLink]);
  }

  const cache = getApolloCache();

  return new ApolloClient({ link, cache });
}

async function isAuthTokenValid(uri: string, authToken?: string): Promise<boolean> {
  if (!authToken) {
    return false;
  }

  const schema = (await queryGql(uri, authToken)) as QueryResult;
  return Boolean(schema?.data);
}

/**
 * Fetches a simple graphql
 * @param {string} uri
 * @param {string} authToken
 * @return {Promise<any>}
 */
async function queryGql(uri: string, authToken: string): Promise<QueryResult> {
  return fetch(uri, {
    method: 'POST',
    headers: {
      Authorization: authToken,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      query: `
      {
        me {
          id
        }
      }
    `,
    }),
  }).then(response => response.json());
}

const fetchWsToken = async () => {
  if (!isTokenAvailable()) {
    return null;
  }

  const url = getGraphQlApiUrl();
  const authToken = getAuthToken();

  const resp = await fetch(url, {
    method: 'POST',
    headers: {
      Authorization: authToken,
      'Content-Type': 'application/json',
    },
    body: `{"query":"mutation generateWsToken { generateWsToken }"}`,
  }).then(response => response.json());

  if (resp.message) {
    return null;
  }

  return resp.data?.generateWsToken;
};

const checkWsConnAvailability = async () => {
  try {
    await retry(getWsCheckPromise);
    return true;
  } catch {
    // throw error in next cycle - skip promise rejection and error will be caught by our global error handler
    setTimeout(() => {
      throw new TrackedError({ content: 'checkWsConnAvailability failed' });
    });
    return false;
  }
};

const getWsCheckPromise = async () => {
  const authToken = getAuthToken();
  const wsToken = await (authToken ? fetchWsToken() : null);

  return new Promise<void>((resolve, reject) => {
    const conn = new WebSocket(`${getWsUrl()}/websocket?token=${wsToken}`);
    conn.onerror = () => {
      conn.close();
      reject();
    };

    conn.onopen = () => {
      conn.close();
      resolve();
    };
  });
};

function retry<T>(fn: () => Promise<T>, retries = 3, err = null): Promise<T> {
  if (retries === 0) {
    return Promise.reject(err);
  }
  return fn().catch(reason => {
    return retry(fn, retries - 1, reason);
  });
}
